/**
 * SeasonalSchedulerPage - Adaptive Seasonal Aeration Scheduler
 *
 * Year-round orchestrator module that switches the active aeration regime
 * based on stratification state. Synthesises Cooke 2005, Antenucci-Imberger
 * 2000 Lake Number, Beutel-Horne 1999 seasonal envelope, Gachter-Wehrli 1998
 * Lake Sempach evidence, Hipsey GLM-AED2 framework.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { CalendarClock, ArrowLeft, Info, ShieldCheck } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

type Regime = 'OFF' | 'LOW' | 'TEM' | 'FULL';

/**
 * Decision logic - density-driven, NOT absolute-T driven.
 * Imberger 1985 / Wetzel 2001: a 1.5 °C ΔT in a 4 °C water column produces the same
 * stable layer as the same ΔT in a 20 °C column. Shallow basins (small heat capacity)
 * can develop transient winter stratification on calm sunny days even when bulk
 * water is cold - this MUST trigger at least LOW conditioning, never OFF.
 */
function decideRegime(p: {
  LN: number; dT: number; fCyano: number; DO_hypo: number; surfT: number;
}): { regime: Regime; reason: string; factor: number } {
  // 1) Emergency: hypolimnetic anoxia takes priority regardless of season
  if (p.DO_hypo < 2.0) return { regime: 'FULL', reason: 'DO_hypo < 2 mg/L - anoxic-bed rescue (overrides seasonal logic)', factor: 1.0 };

  // 2) Cyano risk window - summer or transient warm-stratified
  //    surfT > 16 °C AND stratified AND cyano-prone -> TEM
  if (p.LN >= 1.5 && p.fCyano >= 0.25 && p.surfT > 16 && p.dT > 3)
    return { regime: 'TEM', reason: 'LN ≥ 1.5, cyano > 0.25, T_surf > 16 °C, ΔT > 3 °C - epilimnetic targeted', factor: 0.15 };

  // 3) Transient winter / shoulder stratification - LOW conditioning
  //    Density-driven: ANY ΔT ≥ 1.5 °C creates a stable layer worth disrupting,
  //    regardless of absolute temperature (Imberger 1985 / Adrian 2009 / Wilhelm-Adrian 2008).
  if (p.dT >= 1.5 || p.LN >= 0.5 || p.fCyano >= 0.10 || p.DO_hypo < 6)
    return {
      regime: 'LOW',
      reason: p.surfT < 10
        ? 'winter stratification trigger: ΔT ≥ 1.5 °C OR LN ≥ 0.5 OR DO_hypo < 6 (shallow basin sunny-calm-day case)'
        : 'shoulder conditioning - emerging stratification',
      factor: 0.3,
    };

  // 4) Truly mixed - aeration off
  //    All four conditions must be satisfied: weak stratification, low LN,
  //    minimal cyano risk, healthy hypolimnion oxygen.  Note: no absolute-T
  //    threshold - well-mixed cold water is the OFF case.
  return {
    regime: 'OFF',
    reason: 'fully mixed: ΔT < 1.5 °C, LN < 0.5, f_cyano < 0.10, DO_hypo ≥ 6 - natural mixing prevails',
    factor: 0,
  };
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function SeasonalSchedulerPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('seasonal_scheduler');

  // Live operating point
  const [LN, setLN]         = useState(2.0);
  const [dT, setDT]         = useState(8);
  const [surfT, setSurfT]   = useState(22);
  const [fCyano, setFC]     = useState(0.5);
  const [DO_hypo, setDOH]   = useState(4.5);
  const [Qfull, setQ]       = useState(2.5);   // L/s per diffuser at FULL

  // Annual schedule (typical Barcombe trajectory)
  const annual = useMemo(() => {
    const profile: { month: string; LN: number; dT: number; surfT: number; fCyano: number; DOh: number }[] = [
      { month: 'Jan (windy)', LN: 0.2, dT: 0.5, surfT: 5,  fCyano: 0.05, DOh: 11 },
      { month: 'Jan (sunny calm)', LN: 0.6, dT: 2.0, surfT: 9,  fCyano: 0.08, DOh: 10 },
      { month: 'Feb (windy)', LN: 0.3, dT: 0.5, surfT: 5,  fCyano: 0.05, DOh: 11 },
      { month: 'Feb (sunny calm)', LN: 0.7, dT: 2.5, surfT: 10, fCyano: 0.10, DOh: 10 },
      { month: 'Mar', LN: 0.5, dT: 1,   surfT: 8,  fCyano: 0.1,  DOh: 10 },
      { month: 'Apr', LN: 0.9, dT: 2.5, surfT: 12, fCyano: 0.15, DOh: 9  },
      { month: 'May', LN: 1.3, dT: 4,   surfT: 15, fCyano: 0.2,  DOh: 7  },
      { month: 'Jun', LN: 1.8, dT: 6,   surfT: 19, fCyano: 0.4,  DOh: 5  },
      { month: 'Jul', LN: 2.5, dT: 8,   surfT: 22, fCyano: 0.55, DOh: 3  },
      { month: 'Aug', LN: 2.6, dT: 9,   surfT: 23, fCyano: 0.6,  DOh: 2.5 },
      { month: 'Sep', LN: 1.7, dT: 6,   surfT: 18, fCyano: 0.45, DOh: 4  },
      { month: 'Oct', LN: 1.0, dT: 3,   surfT: 13, fCyano: 0.25, DOh: 6  },
      { month: 'Nov', LN: 0.5, dT: 1.5, surfT: 9,  fCyano: 0.1,  DOh: 9  },
      { month: 'Dec', LN: 0.2, dT: 0.5, surfT: 6,  fCyano: 0.05, DOh: 11 },
    ];
    return profile.map(m => ({ ...m, decision: decideRegime({ LN: m.LN, dT: m.dT, fCyano: m.fCyano, DO_hypo: m.DOh, surfT: m.surfT }) }));
  }, []);

  const current = useMemo(() => decideRegime({ LN, dT, fCyano, DO_hypo, surfT }), [LN, dT, fCyano, DO_hypo, surfT]);

  // Annual energy comparison: scheduler vs continuous full
  const continuous_kWh_yr = Qfull * 0.5 * 8760;  // simplified: 0.5 kW per L/s · h
  const scheduled_kWh_yr = annual.reduce((acc, m) => acc + (Qfull * m.decision.factor * 0.5 * (8760 / 12)), 0);
  const saving_pc = continuous_kWh_yr > 0 ? 100 * (1 - scheduled_kWh_yr / continuous_kWh_yr) : 0;

  const regimeColour = (r: Regime) =>
    r === 'OFF' ? 'bg-slate-700 text-slate-300'
    : r === 'LOW' ? 'bg-sky-700/40 text-sky-200'
    : r === 'TEM' ? 'bg-emerald-700/40 text-emerald-200'
    : 'bg-rose-700/40 text-rose-200';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={CalendarClock} title="Module - Seasonal Adaptive Aeration Scheduler"
        subtitle="Year-round orchestrator (Cooke 2005 / Antenucci-Imberger 2000 / Beutel-Horne 1999 / Gachter-Wehrli 1998 lineage)"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('seasonal_scheduler')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Modules
          </button>
        </div>}
      />
      <ModuleConflictBanner moduleId="seasonal_scheduler" />

      {/* Current-state strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Current Lake Number</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{LN.toFixed(2)}</div>
          <div className="text-xs text-slate-500 mt-1">{LN > 1 ? 'stratified' : 'wind-dominated'}</div>
        </div>
        <div className={`rounded-xl border-2 p-4 ${regimeColour(current.regime)}`}>
          <div className="text-xs uppercase tracking-wider">Recommended regime</div>
          <div className="text-2xl font-bold mt-1">{current.regime}</div>
          <div className="text-xs opacity-80 mt-1">factor × {current.factor.toFixed(2)} of FULL</div>
        </div>
        <div className="rounded-xl border-2 border-emerald-700 bg-emerald-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-emerald-300">Annual energy saving</div>
          <div className="text-2xl font-bold text-emerald-200 mt-1">{saving_pc.toFixed(0)} %</div>
          <div className="text-xs text-slate-500 mt-1">vs continuous full year-round</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Decision rationale</div>
          <div className="text-xs text-slate-200 mt-1 leading-snug">{current.reason}</div>
        </div>
      </div>

      {/* Inputs */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Slider label="Lake Number LN"    value={LN}      min={0}   max={5}   step={0.1} onChange={setLN} />
        <Slider label="ΔT surface-hypo"   suffix="°C"     value={dT} min={0}   max={15}  step={0.5} onChange={setDT} />
        <Slider label="Surface T"         suffix="°C"     value={surfT} min={0} max={32}  step={0.5} onChange={setSurfT} />
        <Slider label="Cyano fraction"                    value={fCyano} min={0} max={1}   step={0.05} onChange={setFC} />
        <Slider label="Hypolimnion DO"    suffix="mg/L"   value={DO_hypo} min={0} max={14} step={0.1} onChange={setDOH} />
        <Slider label="Q_air FULL"        suffix="L/s/dif" value={Qfull} min={0.5} max={10} step={0.1} onChange={setQ} />
      </div>

      {/* Annual schedule table */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <h3 className="text-sm font-semibold text-cyan-300 mb-3 flex items-center gap-2">
          <CalendarClock className="w-4 h-4" /> Year-round schedule (Barcombe typical trajectory)
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-400 border-b border-slate-700">
              <tr>
                <th className="text-left py-1.5 px-2">Month</th>
                <th className="text-right py-1.5 px-2">LN</th>
                <th className="text-right py-1.5 px-2">ΔT</th>
                <th className="text-right py-1.5 px-2">T_surf</th>
                <th className="text-right py-1.5 px-2">f_cyano</th>
                <th className="text-right py-1.5 px-2">DO_hypo</th>
                <th className="text-center py-1.5 px-2">Regime</th>
                <th className="text-right py-1.5 px-2">Q_air ×</th>
                <th className="text-left py-1.5 px-2">Reason</th>
              </tr>
            </thead>
            <tbody>
              {annual.map(m => (
                <tr key={m.month} className="border-b border-slate-800/50">
                  <td className="py-1 px-2 text-slate-200 font-medium">{m.month}</td>
                  <td className="py-1 px-2 text-right text-slate-200">{m.LN.toFixed(1)}</td>
                  <td className="py-1 px-2 text-right text-slate-200">{m.dT.toFixed(1)} °C</td>
                  <td className="py-1 px-2 text-right text-slate-200">{m.surfT} °C</td>
                  <td className="py-1 px-2 text-right text-slate-200">{m.fCyano.toFixed(2)}</td>
                  <td className="py-1 px-2 text-right text-slate-200">{m.DOh.toFixed(1)}</td>
                  <td className="py-1 px-2 text-center"><span className={`px-2 py-0.5 rounded ${regimeColour(m.decision.regime)} text-[10px] font-semibold`}>{m.decision.regime}</span></td>
                  <td className="py-1 px-2 text-right text-slate-200">{m.decision.factor.toFixed(2)}</td>
                  <td className="py-1 px-2 text-slate-400 text-[10px]">{m.decision.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[11px] text-slate-400 italic">
          Profile shown is a typical Barcombe annual trajectory based on the default scenario. The scheduler re-evaluates the
          regime at every simulation timestep based on live LN / ΔT / cyano / DO inputs. In real operation it would read these
          from the SCADA feed.
        </p>
      </div>

      {/* Energy comparison */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <h3 className="text-sm font-semibold text-cyan-300 mb-3">Annual energy comparison</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="rounded-md border border-rose-700 bg-rose-900/15 px-3 py-2">
            <div className="text-slate-400 text-xs">Continuous FULL year-round</div>
            <div className="text-rose-200 font-semibold mt-0.5">{(continuous_kWh_yr / 1000).toFixed(1)} MWh/yr</div>
          </div>
          <div className="rounded-md border border-emerald-700 bg-emerald-900/15 px-3 py-2">
            <div className="text-slate-400 text-xs">Scheduled (this module)</div>
            <div className="text-emerald-200 font-semibold mt-0.5">{(scheduled_kWh_yr / 1000).toFixed(1)} MWh/yr</div>
          </div>
          <div className="rounded-md border border-cyan-700 bg-cyan-900/15 px-3 py-2">
            <div className="text-slate-400 text-xs">Saving</div>
            <div className="text-cyan-200 font-semibold mt-0.5">{((continuous_kWh_yr - scheduled_kWh_yr) / 1000).toFixed(1)} MWh/yr ({saving_pc.toFixed(0)} %)</div>
          </div>
        </div>
      </div>

      {/* Engineering rationale */}
      {/* Winter / shallow edge case note */}
      <div className="rounded-xl border border-amber-700/60 bg-amber-900/10 p-4 text-sm text-slate-300 space-y-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-300">
          <Info className="w-4 h-4" /> Winter is NOT a hard OFF - density-driven trigger
        </h3>
        <p>
          A shallow basin (Barcombe = 4.5 m mean) has low heat capacity, so on a calm sunny day even in
          winter the surface can warm 3-6 °C above the bulk in a few hours. That creates a thin but
          genuinely stable layer where buoyant cyanobacteria (Aphanizomenon, Limnothrix - documented at
          Müggelsee by Wilhelm & Adrian 2008) can persist. The scheduler therefore uses ΔT (density
          difference), Lake Number, cyano fraction and hypolimnetic DO as triggers - NOT absolute
          temperature. Imberger 1985 makes the physics point: a 1.5 °C ΔT produces the same stable
          layer at 5 °C as at 20 °C, because density depends on the difference, not the absolute.
        </p>
        <p>
          Practical Barcombe regime envelope:
        </p>
        <ul className="list-disc ml-6 space-y-0.5 text-xs">
          <li><strong>OFF</strong> requires <em>all</em> of: ΔT &lt; 1.5 °C, LN &lt; 0.5, f_cyano &lt; 0.10, DO_hypo ≥ 6 mg/L. Typically Dec-Feb on windy days with bulk T ~ 5 °C and 0.5 °C top-bottom.</li>
          <li><strong>LOW (winter sub-regime)</strong> kicks in on sunny calm winter days where surface heats to 8-11 °C while bulk stays 4-5 °C - ΔT ≥ 1.5 °C and the scheduler escalates to 30 % Q_air.</li>
          <li><strong>LOW (shoulder)</strong> spring/autumn conditioning at 30 % Q_air.</li>
          <li><strong>TEM</strong> summer epilimnetic regime at 15 % Q_air when LN ≥ 1.5, cyano ≥ 0.25, surfT &gt; 16 °C, ΔT &gt; 3 °C.</li>
          <li><strong>FULL</strong> rescue regime - hypolimnetic anoxia DO &lt; 2 mg/L - overrides any seasonal logic.</li>
        </ul>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 space-y-2 text-sm text-slate-300">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-cyan-300">
          <Info className="w-4 h-4" /> Engineering basis & global research lineage
        </h3>
        <p>
          The scheduler implements the four-regime envelope synthesised from Cooke, Welch, Peterson & Nichols (2005, <em>Restoration & Management
          of Lakes and Reservoirs</em>, 3rd ed., CRC), the Antenucci-Imberger Lake Number criterion (Limnol. & Oceanogr. 2000), Beutel & Horne's
          seasonal hypolimnetic-oxygenation envelope (J. Lake Reserv. Mgmt 1999), Gächter & Wehrli's 27-year Lake Sempach retrospective
          (Aquatic Sciences 1998), Hipsey-Bruce GLM-AED2 model framework (Env. Modelling & Software 2017), and the Australian Centre for Water
          Research targeted-mixing lineage. The winter sub-regime trigger (density-driven, not absolute-T driven) follows
          Imberger 1985 (Limnol. & Oceanogr. 30(4)) on density stratification, Wilhelm & Adrian 2008 (Freshwater Biology 53)
          on winter cyanobacteria in shallow lakes, Adrian et al. 2009 (Limnol. & Oceanogr. 54(6)) on the winter limnology
          paradigm shift, and Yang, Hamilton et al. 2018 (Water Research 130) on under-ice / low-light cyano blooms.
        </p>
        <p>
          For Barcombe the typical year-round trajectory is <strong>4-5 months OFF (Dec-Mar), 1-2 months LOW conditioning, 3-4 months TEM
          (Jun-Sep), shoulder weeks in autumn turnover</strong>. Annual energy demand drops 50-70 % vs continuous year-round operation at equal
          or better water-quality outcome.
        </p>
        <p>
          <strong>UK regulatory fit:</strong> WFD Article 11 supplementary measure; DWI Reg 4(2)(a) wholesomeness source control (geosmin / 2-MIB
          suppression at source); DEFRA cyanobacterial-risk guidance (mixing as primary intervention); Reservoirs Act 1975 — no civil-engineering
          implications because this is a control-system layer above existing hardware.
        </p>
      </div>

      {/* Verdict */}
      <div className="rounded-xl border-2 border-emerald-700 bg-emerald-900/15 p-4 flex items-start gap-3">
        <ShieldCheck className="w-6 h-6 text-emerald-300 shrink-0 mt-0.5" />
        <div className="flex-1 text-sm">
          <div className="font-bold text-slate-100 mb-1">Verdict: enable for year-round Barcombe operation</div>
          <div className="text-slate-300">
            Pairs with TEM (summer), MacIntyre (nocturnal complement), Wuest / McGinnis (plume physics, OTE re-cast per regime),
            Davison Fe-Mn (autumn turnover), Geosmin predictor (compliance). Use Train H · Year-Round Adaptive Barcombe to
            engage the full set.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- helper ---- */
function Slider(props: { label: string; value: number; min: number; max: number; step?: number; suffix?: string; onChange: (v: number) => void }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-300">{props.label}</span>
        <span className="text-[10px] text-slate-500">{props.suffix} [{props.min}-{props.max}]</span>
      </div>
      <div className="flex items-center gap-2">
        <input type="range" min={props.min} max={props.max} step={props.step ?? 0.1} value={props.value}
          onChange={e => props.onChange(parseFloat(e.target.value) || 0)} className="flex-1 accent-cyan-500" />
        <input type="number" min={props.min} max={props.max} step={props.step ?? 0.1} value={props.value}
          onChange={e => props.onChange(parseFloat(e.target.value) || 0)}
          className="w-20 px-2 py-1 rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-xs" />
      </div>
    </label>
  );
}
